import React, { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { getOneLab } from "../../service/api"
import jsPDF from "jspdf";
import "jspdf-autotable";
import { Card, Col, Container, Row, CardBody, Form } from "reactstrap"

const SectionTitle = ({ children, color = "#DC4C64" }) => (
  <h6 style={{
    color,
    fontWeight: 700,
    borderBottom: `2px solid ${color}`,
    paddingBottom: 4,
    marginBottom: 12,
    marginTop: 4,
    letterSpacing: 0.3,
  }}>{children}</h6>
)

const Field = ({ label, value }) => (
  <div style={{ marginBottom: 6 }}>
    <span style={{ fontWeight: 600, fontSize: 13, color: "#555" }}>{label}: </span>
    <span style={{ fontSize: 13, color: "#222" }}>{value || <span style={{ color: "#bbb" }}>—</span>}</span>
  </div>
)

const TableRow = ({ label, value, accent }) => (
  <tr>
    <td style={{ fontWeight: 600, fontSize: 12, color: accent || "#555", padding: "4px 8px", whiteSpace: "nowrap", width: 120 }}>{label}</td>
    <td style={{ fontSize: 12, color: "#222", padding: "4px 8px" }}>{value || "—"}</td>
  </tr>
)

export default function LabDetails() {
  const { id } = useParams()
  const [lab, setLab] = useState(null)

  useEffect(() => {
    const fetchLab = async () => {
      try {
        const response = await getOneLab(id)
        setLab(response.data)
      } catch (error) {
        console.error("There was an error fetching the lab data!", error)
      }
    }
    fetchLab()
  }, [id])

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    let currentY = 10;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    const titleText = "LAB ORDER REPORT";
    const textWidth = doc.getTextWidth(titleText);
    const centerX = (pageWidth - textWidth) / 2;
    doc.text(titleText, centerX, currentY);
    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    doc.line(centerX, currentY + 2, centerX + textWidth, currentY + 2);
    currentY += 20;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);

    const contentStyle = () => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
    };

    const drawHeader = (base64Logo) => {
      doc.setFontSize(10);
      doc.text("Practice Name:", 10, currentY);
      doc.text("VC reference No.:", 10, currentY + 6);
      doc.text("Order No:", 10, currentY + 12);
      doc.text("Payment Details:", 10, currentY + 18);
      doc.text("Est. Delivery Date:", 10, currentY + 24);
      doc.text("Delivery Information:", 10, currentY + 30);
      doc.setFont("bold", "helvetica");
      doc.text(lab.practisc_name || "", 45, currentY);
      doc.text(lab.vc_reference || "", 45, currentY + 6);
      doc.text(lab.order_no || "", 45, currentY + 12);
      doc.text(lab.payment_details || "", 45, currentY + 18);
      doc.text(lab.delivery_date || "", 45, currentY + 24);
      doc.text(lab.delivery_info || "", 45, currentY + 30);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      if (base64Logo) {
        doc.addImage(base64Logo, 'JPEG', pageWidth - 70, 20, 20, 20);
      }
      doc.text(lab.patient_id?.patientID?.clinicname?.name || "", pageWidth - 70, currentY + 18);
      doc.text(lab.patient_id?.patientID?.clinicname?.email || "", pageWidth - 70, currentY + 22);
      doc.text(lab.patient_id?.patientID?.clinicname?.contactno || "", pageWidth - 70, currentY + 27);
      doc.text(lab.patient_id?.patientID?.clinicname?.address || "", pageWidth - 70, currentY + 31);
      currentY += 35;
    };

    const drawDivider = () => {
      doc.setDrawColor(200);
      doc.line(10, currentY, pageWidth - 10, currentY);
      currentY += 5;
    };

    const drawTable = (headers, data) => {
      doc.autoTable({
        startY: currentY,
        head: [headers],
        body: data,
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 2 },
        headStyles: { fillColor: [201, 104, 104], textColor: [255, 255, 255] },
        tableWidth: "auto",
        margin: { left: 10, right: 10 },
      });
      currentY = doc.lastAutoTable.finalY + 10;
    };

    const loadImageAsBase64 = (imagePath, callback) => {
      const xhr = new XMLHttpRequest();
      xhr.onload = function () {
        const reader = new FileReader();
        reader.onloadend = function () { callback(reader.result); };
        reader.readAsDataURL(xhr.response);
      };
      xhr.open('GET', imagePath);
      xhr.responseType = 'blob';
      xhr.send();
    };

    let logoPath = lab.patient_id?.patientID?.clinicname?.logo || "";
    if (logoPath) {
      logoPath = "\\" + logoPath.split("\\").slice(-2).join("\\");
    }
    const logoURL = `https://oapi.ehrhub.co.za${logoPath}`;

    loadImageAsBase64(logoURL, (base64Logo) => {
      // load signature as base64, then build the full PDF
      const sigURL = lab.signature ? `https://oapi.ehrhub.co.za/${lab.signature}` : null;

      const buildPDF = (base64Sig) => {
        drawHeader(base64Logo);
        contentStyle();

        const drawTableSideBySide = (headers1, data1, headers2, data2) => {
          const columnWidth = pageWidth / 2 - 20;
          doc.autoTable({
            startY: currentY, head: [headers2], body: data2, theme: "grid",
            styles: { fontSize: 10, cellPadding: 2 },
            headStyles: { fillColor: [201, 104, 104], textColor: [255, 255, 255] },
            bodyStyles: { fontSize: 9, cellPadding: 0.6 },
            tableWidth: columnWidth, margin: { left: 10 },
          });
          let firstTableY = doc.lastAutoTable.finalY;
          doc.autoTable({
            startY: currentY, head: [headers1], body: data1, theme: "grid",
            styles: { fontSize: 10, cellPadding: 2 },
            headStyles: { fillColor: [201, 104, 104], textColor: [255, 255, 255] },
            tableWidth: columnWidth, margin: { left: pageWidth / 2 + 10 },
          });
          let secondTableY = doc.lastAutoTable.finalY;
          currentY = Math.max(firstTableY, secondTableY) + 10;
        };

        drawTableSideBySide(
          ["Patient Details", ""],
          [
            ["Patient Name", lab.patient_name],
            ["MPI No", lab.patient_id?.patientID?.mpino],
            ["Date Of Birth", lab.patient_id?.patientID?.dob],
            ["Age", lab.patient_id?.patientID?.age],
            ["Gender", lab.patient_id?.patientID?.gender],
            ["Contact Number", lab.patient_id?.patientID?.mobile]
          ],
          ["Frames Information", ""],
          [
            ["Frame", lab.frame],
            ["Frame Color", lab.frame_color],
            ["Size", lab.frame_size],
            ["Material", lab.frame_material],
            ["Type", lab.frame_type],
            ["Shape", lab.frame_shape],
            ["Frame Brand", lab.frame_brand],
            ["PD", lab.pd],
            ["PD Left", lab.pd_left],
            ["PD Right", lab.pd_right]
          ]
        );

        const framHeaders = ["", "Sphere", "Cyl", "Axis", "Add", "Seg. Height"];
        const framData = [
          ["OD", lab.od_spere, lab.od_cyl, lab.od_axis, lab.od_add, lab.od_seg_height],
          ["OS", lab.os_spere, lab.os_cyl, lab.os_axis, lab.os_add, lab.os_seg_height]
        ];
        drawTable(framHeaders, framData);

        contentStyle();
        drawTable(["Lens Details — OD (Right Eye)", ""], [
          ["Material", lab.lens_material_od],
          ["Vision", lab?.lens_vision_od?.vision],
          ["Sub-Type", lab?.lens_subvision_od?.sub_type],
          ["Coating", lab.lens_type_od],
          ["Photochromic", lab.extra_od === "yes" ? "Yes" : "No"],
          ["OD Total", `R ${parseFloat(lab.total_od || 0).toFixed(2)}`],
        ]);

        drawTable(["Lens Details — OS (Left Eye)", ""], [
          ["Material", lab.lens_material_os],
          ["Vision", lab?.lens_vision_os?.vision],
          ["Sub-Type", lab?.lens_subvision_os?.sub_type],
          ["Coating", lab.lens_type_os],
          ["Photochromic", lab.extra_os === "yes" ? "Yes" : "No"],
          ["OS Total", `R ${parseFloat(lab.total_os || 0).toFixed(2)}`],
        ]);

        const lensHeaders = ["", "Vertical (A)", "Base Direction", "Horizontal (A)", "Base Direction"];
        const lensData = [
          ["OD [Right]", lab.od_vertical, lab.od_vb_direction, lab.od_horizontal, lab.od_hb_direction],
          ["OS [Left]", lab.os_vertical, lab.os_vb_direction, lab.os_horizontal, lab.os_hb_direction]
        ];
        drawTable(lensHeaders, lensData);

        const pDHeaders = ["PD", "PD Left", "PD Right"];
        const pDData = [[lab.pre_pd, lab.pre_pd_left, lab.pre_pd_right]];
        drawTable(pDHeaders, pDData);

        // ensure enough space for footer (80px needed)
        if (currentY + 80 > doc.internal.pageSize.height) {
          doc.addPage();
          currentY = 20;
        }

        const drawFooterInTwoColumns = () => {
          const leftColumnX = 10;
          const rightColumnX = pageWidth / 2 + 10;
          const labelOffset = 40;
          const imageWidth = 60;
          const imageHeight = 30;
          // FIX: use outer currentY — NOT a local shadow
          let leftY = currentY;
          let rightY = currentY;

          const labelsLeftColumn = [
            `Practice Name: ${lab.practice_name || '____________________'}`,
            `PX Number: ${lab.px_number || '____________________'}`,
            `Additional notes: ${lab.additional_notes || '____________________'}`,
            `Authorized By: ${lab.authorized_by || '____________________'}`,
          ];

          for (let i = 0; i < labelsLeftColumn.length; i++) {
            const [label, value] = labelsLeftColumn[i].split(': ');
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            doc.text(`${label}:`, leftColumnX, leftY);
            doc.setFont("helvetica", "normal");
            doc.text(value || '____________________', leftColumnX + labelOffset, leftY);
            leftY += 10;
          }

          doc.setFont("helvetica", "bold");
          doc.setFontSize(12);
          doc.text("Final Total:", rightColumnX, rightY);
          doc.setLineWidth(0.5);
          doc.line(rightColumnX, rightY + 2, rightColumnX + 30, rightY + 2);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(11);
          doc.text(`R ${lab.total || '0.00'}`, rightColumnX + labelOffset, rightY);
          rightY += 20;

          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          doc.text("Signature:", rightColumnX, rightY);
          rightY += 5;
          // FIX: use pre-loaded base64 — not a raw URL
          if (base64Sig) {
            try {
              doc.addImage(base64Sig, "JPEG", rightColumnX, rightY, imageWidth, imageHeight);
            } catch (_) {}
          }
        };

        drawFooterInTwoColumns();
        doc.save(`lab-order-${lab?._id}.pdf`);
      };

      if (sigURL) {
        loadImageAsBase64(sigURL, buildPDF);
      } else {
        buildPDF(null);
      }
    });
  };

  if (!lab) return (
    <div className="page-content">
      <Container fluid>
        <div style={{ textAlign: "center", padding: 60, color: "#888" }}>
          <i className="mdi mdi-loading mdi-spin" style={{ fontSize: 32 }}></i>
          <p className="mt-2">Loading lab order...</p>
        </div>
      </Container>
    </div>
  )

  const clinic = lab.patient_id?.patientID?.clinicname
  const patient = lab.patient_id?.patientID
  const totalGrand = parseFloat(lab.total || 0)
  const totalOD = parseFloat(lab.total_od || 0)
  const totalOS = parseFloat(lab.total_os || 0)
  const framePrice = parseFloat(lab.frame_price || 0)

  return (
    <div className="page-content">
      <Container fluid>

        {/* Top bar */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 style={{ margin: 0, fontWeight: 700 }}>Lab Order Details</h4>
          <div>
            <button className="btn btn-success mx-2" onClick={() => handleDownloadPDF(id)}>
              <i className="mdi mdi-download font-size-15 me-1"></i> PDF
            </button>
            <Link to="/LabOrderList" className="btn btn-danger">
              <i className="mdi mdi-format-list-bulleted me-1"></i> List
            </Link>
          </div>
        </div>

        <Form>

          {/* ── ORDER HEADER ── */}
          <Card className="mb-3" style={{ border: "1px solid #f0f0f0" }}>
            <CardBody>
              <SectionTitle>Order Information</SectionTitle>
              <Row>
                <Col md={4}>
                  <Field label="Order No" value={lab.order_no} />
                  <Field label="Date" value={lab.date} />
                  <Field label="VC Reference No" value={lab.vc_reference} />
                  <Field label="Order Type" value={lab.order_type} />
                </Col>
                <Col md={4}>
                  <Field label="Practice Name" value={lab.practisc_name} />
                  <Field label="Patient Name" value={lab.patient_name} />
                  <Field label="Payment Details" value={lab.payment_details} />
                  <Field label="Delivery Date" value={lab.delivery_date} />
                </Col>
                <Col md={4}>
                  <Field label="Delivery Info" value={lab.delivery_info} />
                  {clinic && (
                    <>
                      <Field label="Clinic" value={clinic.name} />
                      <Field label="Email" value={clinic.email} />
                      <Field label="Contact" value={clinic.contactno} />
                    </>
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* ── PATIENT DETAILS ── */}
          {patient && (
            <Card className="mb-3" style={{ border: "1px solid #f0f0f0" }}>
              <CardBody>
                <SectionTitle>Patient Details</SectionTitle>
                <Row>
                  <Col md={3}><Field label="MPI No" value={patient.mpino} /></Col>
                  <Col md={3}><Field label="Date of Birth" value={patient.dob} /></Col>
                  <Col md={3}><Field label="Age" value={patient.age} /></Col>
                  <Col md={3}><Field label="Gender" value={patient.gender} /></Col>
                  <Col md={3}><Field label="Contact" value={patient.mobile} /></Col>
                </Row>
              </CardBody>
            </Card>
          )}

          {/* ── FRAME DETAILS ── */}
          <Card className="mb-3" style={{ border: "1px solid #f0f0f0" }}>
            <CardBody>
              <SectionTitle>Frame Details</SectionTitle>
              <Row>
                <Col md={6}>
                  <Row>
                    <Col md={6}><Field label="Frame" value={lab.frame} /></Col>
                    <Col md={6}><Field label="Brand" value={lab.frame_brand} /></Col>
                    <Col md={6}><Field label="Color" value={lab.frame_color} /></Col>
                    <Col md={6}><Field label="Size" value={lab.frame_size} /></Col>
                    <Col md={6}><Field label="Material" value={lab.frame_material} /></Col>
                    <Col md={6}><Field label="Type" value={lab.frame_type} /></Col>
                    <Col md={6}><Field label="Shape" value={lab.frame_shape} /></Col>
                    {lab.framecode && <Col md={6}><Field label="Frame Code" value={lab.framecode} /></Col>}
                    {lab.frame_price && (
                      <Col md={6}>
                        <div style={{ marginBottom: 6 }}>
                          <span style={{ fontWeight: 600, fontSize: 13, color: "#555" }}>Frame Price: </span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: "#16a34a" }}>R {framePrice.toFixed(2)}</span>
                        </div>
                      </Col>
                    )}
                  </Row>
                </Col>
                <Col md={6}>
                  <SectionTitle color="#3b82f6">PD Data</SectionTitle>
                  <Row>
                    <Col md={4}><Field label="PD" value={lab.pd} /></Col>
                    <Col md={4}><Field label="PD Left" value={lab.pd_left} /></Col>
                    <Col md={4}><Field label="PD Right" value={lab.pd_right} /></Col>
                    <Col md={12}><Field label="Two PD Number" value={lab.two_pd_number} /></Col>
                  </Row>
                </Col>
              </Row>

              {/* Frame capture image */}
              {lab.frame_image && (
                <div className="mt-3">
                  <span style={{ fontWeight: 600, fontSize: 13, color: "#555" }}>Frame Image: </span>
                  <img src={lab.frame_image} alt="Frame" style={{ height: 80, borderRadius: 6, marginTop: 4, border: "1px solid #eee" }} />
                </div>
              )}
            </CardBody>
          </Card>

          {/* ── PRESCRIPTION ── */}
          <Card className="mb-3" style={{ border: "1px solid #f0f0f0" }}>
            <CardBody>
              <SectionTitle>Prescription / Rx</SectionTitle>
              <div style={{ overflowX: "auto" }}>
                <table className="table table-bordered table-sm" style={{ fontSize: 13 }}>
                  <thead style={{ background: "#DC4C64", color: "#fff" }}>
                    <tr>
                      <th style={{ width: 60 }}></th>
                      <th>Sphere</th>
                      <th>Cyl</th>
                      <th>Axis</th>
                      <th>Add</th>
                      <th>Seg Height</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ fontWeight: 700, color: "#DC4C64" }}>OD</td>
                      <td>{lab.od_spere || "—"}</td>
                      <td>{lab.od_cyl || "—"}</td>
                      <td>{lab.od_axis || "—"}</td>
                      <td>{lab.od_add || "—"}</td>
                      <td>{lab.od_seg_height || "—"}</td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: 700, color: "#3b82f6" }}>OS</td>
                      <td>{lab.os_spere || "—"}</td>
                      <td>{lab.os_cyl || "—"}</td>
                      <td>{lab.os_axis || "—"}</td>
                      <td>{lab.os_add || "—"}</td>
                      <td>{lab.os_seg_height || "—"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>

          {/* ── LENS DETAILS ── */}
          <Card className="mb-3" style={{ border: "1px solid #f0f0f0" }}>
            <CardBody>
              <SectionTitle>Lens Details</SectionTitle>

              {/* Shared lens code / tint */}
              {(lab.lens_material || lab.lens_tint || lab.lenscode) && (
                <Row className="mb-3">
                  {lab.lenscode && <Col md={3}><Field label="Lens Code" value={lab.lenscode} /></Col>}
                  {lab.lens_material && <Col md={3}><Field label="Lens Material" value={lab.lens_material} /></Col>}
                  {lab.lens_tint && <Col md={3}><Field label="Tint" value={lab.lens_tint} /></Col>}
                </Row>
              )}

              <Row>
                {/* OD */}
                <Col md={6}>
                  <div style={{
                    border: "1px solid #DC4C64",
                    borderRadius: 8,
                    padding: "12px 16px",
                    background: "#fff8f8",
                  }}>
                    <div style={{ fontWeight: 700, color: "#DC4C64", fontSize: 14, marginBottom: 10 }}>
                      OD — Right Eye
                    </div>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <tbody>
                        <TableRow label="Material" value={lab.lens_material_od} accent="#DC4C64" />
                        <TableRow label="Vision" value={lab?.lens_vision_od?.vision} />
                        <TableRow label="Sub-Type" value={lab?.lens_subvision_od?.sub_type} />
                        <TableRow label="Coating" value={lab.lens_type_od ? lab.lens_type_od.toUpperCase() : null} />
                        <TableRow label="Photochromic" value={lab.extra_od === "yes" ? "Yes" : "No"} />
                        <tr style={{ borderTop: "1px solid #f0c0c0" }}>
                          <td style={{ fontWeight: 700, fontSize: 13, color: "#DC4C64", padding: "6px 8px" }}>OD Total</td>
                          <td style={{ fontWeight: 700, fontSize: 13, color: "#DC4C64", padding: "6px 8px" }}>
                            R {totalOD.toFixed(2)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Col>

                {/* OS */}
                <Col md={6}>
                  <div style={{
                    border: "1px solid #3b82f6",
                    borderRadius: 8,
                    padding: "12px 16px",
                    background: "#f5f8ff",
                  }}>
                    <div style={{ fontWeight: 700, color: "#3b82f6", fontSize: 14, marginBottom: 10 }}>
                      OS — Left Eye
                    </div>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <tbody>
                        <TableRow label="Material" value={lab.lens_material_os} accent="#3b82f6" />
                        <TableRow label="Vision" value={lab?.lens_vision_os?.vision} />
                        <TableRow label="Sub-Type" value={lab?.lens_subvision_os?.sub_type} />
                        <TableRow label="Coating" value={lab.lens_type_os ? lab.lens_type_os.toUpperCase() : null} />
                        <TableRow label="Photochromic" value={lab.extra_os === "yes" ? "Yes" : "No"} />
                        <tr style={{ borderTop: "1px solid #b0c8f8" }}>
                          <td style={{ fontWeight: 700, fontSize: 13, color: "#3b82f6", padding: "6px 8px" }}>OS Total</td>
                          <td style={{ fontWeight: 700, fontSize: 13, color: "#3b82f6", padding: "6px 8px" }}>
                            R {totalOS.toFixed(2)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* ── PRISM ── */}
          {(lab.add_prism || lab.od_vertical || lab.os_vertical) && (
            <Card className="mb-3" style={{ border: "1px solid #f0f0f0" }}>
              <CardBody>
                <SectionTitle>Prism Details</SectionTitle>
                <Field label="Add Prism" value={lab.add_prism} />
                <div style={{ overflowX: "auto", marginTop: 8 }}>
                  <table className="table table-bordered table-sm" style={{ fontSize: 13 }}>
                    <thead style={{ background: "#DC4C64", color: "#fff" }}>
                      <tr>
                        <th style={{ width: 70 }}></th>
                        <th>Vertical (A)</th>
                        <th>Base Direction</th>
                        <th>Horizontal (A)</th>
                        <th>Base Direction</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ fontWeight: 700, color: "#DC4C64" }}>OD</td>
                        <td>{lab.od_vertical || "—"}</td>
                        <td>{lab.od_vb_direction || "—"}</td>
                        <td>{lab.od_horizontal || "—"}</td>
                        <td>{lab.od_hb_direction || "—"}</td>
                      </tr>
                      <tr>
                        <td style={{ fontWeight: 700, color: "#3b82f6" }}>OS</td>
                        <td>{lab.os_vertical || "—"}</td>
                        <td>{lab.os_vb_direction || "—"}</td>
                        <td>{lab.os_horizontal || "—"}</td>
                        <td>{lab.os_hb_direction || "—"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
          )}

          {/* ── PREVIOUS PRESCRIPTION ── */}
          {(lab.pre_od_sphere || lab.pre_os_sphere) && (
            <Card className="mb-3" style={{ border: "1px solid #f0f0f0" }}>
              <CardBody>
                <SectionTitle>Previous Prescription</SectionTitle>
                <div style={{ overflowX: "auto" }}>
                  <table className="table table-bordered table-sm" style={{ fontSize: 13 }}>
                    <thead style={{ background: "#DC4C64", color: "#fff" }}>
                      <tr>
                        <th style={{ width: 60 }}></th>
                        <th>Sphere</th>
                        <th>Cyl</th>
                        <th>Axis</th>
                        <th>Add</th>
                        <th>Seg Height</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ fontWeight: 700, color: "#DC4C64" }}>OD</td>
                        <td>{lab.pre_od_sphere || "—"}</td>
                        <td>{lab.pre_od_cyl || "—"}</td>
                        <td>{lab.pre_od_axis || "—"}</td>
                        <td>{lab.pre_od_aad || "—"}</td>
                        <td>{lab.pre_od_segheight || "—"}</td>
                      </tr>
                      <tr>
                        <td style={{ fontWeight: 700, color: "#3b82f6" }}>OS</td>
                        <td>{lab.pre_os_sphere || "—"}</td>
                        <td>{lab.pre_os_cyl || "—"}</td>
                        <td>{lab.pre_os_axis || "—"}</td>
                        <td>{lab.pre_os_aad || "—"}</td>
                        <td>{lab.pre_os_segheight || "—"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <Row className="mt-2">
                  <Col md={3}><Field label="Pre PD" value={lab.pre_pd} /></Col>
                  <Col md={3}><Field label="Pre PD Left" value={lab.pre_pd_left} /></Col>
                  <Col md={3}><Field label="Pre PD Right" value={lab.pre_pd_right} /></Col>
                </Row>
              </CardBody>
            </Card>
          )}

          {/* ── ADDITIONAL DETAILS + TOTAL + SIGNATURE ── */}
          <Card className="mb-3" style={{ border: "1px solid #f0f0f0" }}>
            <CardBody>
              <Row>
                {/* Left: additional details */}
                <Col md={6}>
                  <SectionTitle>Additional Details</SectionTitle>
                  <Field label="Authorized By" value={lab.authorized_by} />
                  <Field label="Practice Name" value={lab.practice_name} />
                  <Field label="PX Number" value={lab.px_number} />
                  <Field label="Additional Notes" value={lab.additional_notes} />
                </Col>

                {/* Right: total + signature */}
                <Col md={6}>
                  <SectionTitle>Total &amp; Signature</SectionTitle>

                  {/* Pricing breakdown */}
                  <div style={{
                    background: "#f9f9f9",
                    border: "1px solid #eee",
                    borderRadius: 8,
                    padding: "10px 14px",
                    marginBottom: 14,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 13, color: "#555" }}>OD — Right Eye</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#DC4C64" }}>R {totalOD.toFixed(2)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 13, color: "#555" }}>OS — Left Eye</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "#3b82f6" }}>R {totalOS.toFixed(2)}</span>
                    </div>
                    {framePrice > 0 && (
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                        <span style={{ fontSize: 13, color: "#555" }}>Frame</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#16a34a" }}>R {framePrice.toFixed(2)}</span>
                      </div>
                    )}
                    <div style={{
                      display: "flex", justifyContent: "space-between",
                      borderTop: "2px solid #DC4C64", paddingTop: 8,
                    }}>
                      <span style={{ fontWeight: 700, fontSize: 15 }}>Grand Total</span>
                      <span style={{ fontWeight: 700, fontSize: 15, color: "#DC4C64" }}>
                        R {totalGrand.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Signature */}
                  {lab.signature && (
                    <div>
                      <span style={{ fontWeight: 600, fontSize: 13, color: "#555" }}>Signature:</span>
                      <div style={{ marginTop: 6, border: "1px solid #eee", borderRadius: 6, padding: 4, display: "inline-block" }}>
                        <img
                          src={`https://oapi.ehrhub.co.za/${lab.signature}`}
                          alt="Signature"
                          style={{ height: 90, maxWidth: "100%" }}
                        />
                      </div>
                    </div>
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>

        </Form>
      </Container>
    </div>
  )
}
